import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";

// Define the shape of the decoded JWT payload
interface DecodedToken {
  username: string;
  role: string;
  iat: number;
}

// Define the component's props to accept the login handler from App.tsx
type Props = {
  onLoginSuccess: (token: string, username: string, role: string) => void;
};

export default function AuthCallback({ onLoginSuccess }: Props) {
  const navigate = useNavigate();

  useEffect(() => {
    // 1. Parse the token from the URL's query parameters
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      try {
        // 2. Decode the token to get user info
        const decoded = jwtDecode<DecodedToken>(token);
        
        // 3. Call the main login handler in App.tsx to set the global state
        onLoginSuccess(token, decoded.username, decoded.role);
        
        // 4. Redirect to the main dashboard
        navigate('/', { replace: true });

      } catch (error) {
        console.error("Failed to decode token from SSO callback.", error);
        navigate('/login', { replace: true });
      }
    } else {
      // If no token is found, something went wrong. Go back to login.
      console.error("SSO Callback Error: No token found in URL.");
      navigate('/login', { replace: true });
    }
  // The navigate and onLoginSuccess functions are stable, so we can disable the lint warning.
  // We only want this to run once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render a simple loading state while the logic runs
  return (
    <div className="bg-canvas flex h-full items-center justify-center p-6">
      <div
        role="status"
        className="animate-fade-in flex flex-col items-center gap-3.5 text-center"
      >
        <span
          aria-hidden="true"
          className="border-line border-t-accent h-7 w-7 animate-spin rounded-full border-[3px]"
        />
        <p className="text-ink-muted text-sm font-medium">
          Signing you in&hellip;
        </p>
      </div>
    </div>
  );
};

